<template>
    <div class="ui-table" role="table"
        v-bind:class="[
            `_theme_${theme}`,
            { _autosize: !sizesProvided }
        ]"
        >
        <template v-if="rows.length > 0">
            <div class="ui-table__container">
                <div class="ui-table__header"
                    v-if="columns && columns.length > 0"
                    v-bind:style="{
                        paddingLeft: layout && layout.rowPaddingLeft,
                        paddingRight: layout && layout.rowPaddingRight,
                    }"
                    >
                    <div class="ui-table__column"
                        v-for="(column, index) in columns"
                        v-bind:key="index"
                        v-bind:class="{ _wide: column.wide }"
                        v-bind:style="{
                            minWidth: column.minWidth || (layout && layout.minWidth),
                            flex: '1 1 ' + column.width,
                            ...column.style,
                        }"
                        >
                        <div class="ui-table__header-cell">
                            <span v-text="column.title" />
                            <span class="ui-table__header-cell-icon"
                                v-if="column.sortable"
                                >
                                <span class="icon icon-sort-desc" />
                            </span>
                        </div>
                    </div>
                </div>
                <div class="ui-table__body">
                    <div class="ui-table__body-row-wrapper"
                        v-for="(row, rowIndex) in normalizedRows"
                        v-bind:key="rowIndex"
                        v-bind:class="{
                            _expanded: isExpanded(rowIndex),
                            _with_details: !!row.details,
                        }"
                        v-bind:style="{
                            marginBottom: layout && layout.rowMarginBottom,
                        }"
                        >
                        <div class="ui-table__body-row"
                            v-bind:style="{
                                paddingLeft: layout && layout.rowPaddingLeft,
                                paddingRight: layout && layout.rowPaddingRight,
                            }"
                            >
                            <div class="ui-table__column"
                                v-for="(cell, index) in row.cells"
                                v-bind:key="index"
                                v-bind:class="{
                                    _wide: columns && columns[index] && columns[index].wide,
                                    _fixed: columns && columns[index] && columns[index].fixed,
                                }"
                                v-bind:style="{
                                    minWidth: (columns && columns[index] && columns[index].minWidth) || (layout && layout.minWidth),
                                    flex: '1 1 ' + (columns && columns[index] && columns[index].width),
                                    ...(columns && columns[index] && columns[index].style)
                                }"
                                >
                                <template v-if="cell.type === 'component'">
                                    <component v-bind:is="cell.component"
                                        v-bind="cell.props"
                                    />
                                </template>
                                <template v-else>
                                    <div v-text="cell.props.text" />
                                </template>
                                <div class="ui-table__details-trigger"
                                    v-if="row.details && index === detailsTriggerColumn"
                                    v-on:click="toggleDetails(rowIndex)"
                                    >
                                    <span class="ui-table__details-trigger-text"
                                        v-text="detailsLabel"
                                    />
                                    <span class="ui-table__details-trigger-icon">
                                        <icon name="triangle" />
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="ui-table__details"
                            v-if="row.details && isExpanded(rowIndex)"
                            >
                            <div class="ui-table__details-wrapper">
                                <ui-table v-bind="row.details" theme="nested" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="ui-table__empty"
                v-text="'Записей не найдено'"
            />
        </template>
    </div>
</template>

<script>
export default {
    name: 'ui-table',
    props: {
        layout: {
            type: Object,
        },
        columns: {
            type: Array,
            default: null,
        },
        rows: {
            type: Array,
            default: () => [],
        },
        theme: {
            type: String,
            default: 'default',
        },
        detailsLabel: {
            type: String,
            default: 'Детализация',
        },
        detailsTriggerColumn: {
            type: Number,
            default: 0,
        },
    },
    data: () => ({
        sorting: {
            by: null,
            order: 'desc',
        },
        expandedRows: {},
    }),
    computed: {
        sizesProvided() {
            return this.columns && (this.columns.find(x => x.wide) || this.columns.every(x => x.width));
        },
        normalizedRows() {
            return this.rows.map(row => Array.isArray(row)
                ? { cells: row, details: null }
                : { cells: row.cells || [], details: row.details || null }
            );
        },
    },
    methods: {
        isExpanded(index) {
            return !!this.expandedRows[index];
        },
        toggleDetails(index) {
            this.$set(this.expandedRows, index, !this.expandedRows[index]);
        },
    },
};
</script>

<style scoped lang="less">
@import '~theme';

.ui-table {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    height: 100%;
    overflow: auto;
    &._theme_dark &__body-row {
        background-color: @color-gray-lighter;
    }
    &._theme_secondary &__body-row-wrapper {
        border: 1px solid @color-gray-light;
        border-radius: @border-radius-lg;

        background-color: @color-gray-lightest;
    }
    &._theme_secondary &__header-cell {
        color: @color-gray-main;
    }
    &._theme_nested {
        overflow: visible;
    }
    &._theme_nested &__header {
        padding: 12px 16px;
    }
    &._theme_nested &__header-cell {
        color: @color-gray-main;
    }
    &._theme_nested &__body-row-wrapper {
        margin-bottom: 0;
        border: 0;
        border-bottom: 1px solid @color-gray-light;
        border-radius: 0;
        background-color: transparent;
        &:first-child {
            border-top: 1px solid @color-gray-light;
        }
        &:last-child {
            border-bottom: none;
        }
    }
    &._theme_nested &__body-row {
        padding: 14px 16px;
        border-radius: 0;

        background-color: transparent;
    }
    &__container {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        width: 100%;
    }
    &__header {
        .typography-caption();

        flex: 0 0 auto;
        display: flex;
        justify-content: flex-start;
        align-items: baseline;
        padding: 31px 16px 12px;

        text-transform: uppercase;
        &-cell {
            display: flex;
            justify-content: flex-start;
            align-items: center;

            font-weight: bold;
            &-icon {
                display: inline-block;
                margin-left: 6px;

                font-size: 1.8rem;

                cursor: pointer;
            }
        }
    }
    &__body {
        flex: 1 1 100%;
        &-row-wrapper {
            margin-bottom: 8px;
        }
        &-row {
            .typography-body();

            display: flex;
            justify-content: flex-start;
            align-items: center;
            width: 100%;
            padding: 16px 16px 15px;
            border-radius: @border-radius-lg;

            background-color: @color-gray-lightest;
        }
    }
    &__details {
        padding: 16px 16px 16px;
        &-trigger {
            .typography-caption();

            display: inline-flex;
            justify-content: flex-start;
            align-items: center;
            margin-top: 4px;

            font-weight: bold;
            text-transform: uppercase;

            color: @color-primary;

            user-select: none;
            cursor: pointer;

            transition: color @duration-faster ease-in-out;
            &:hover {
                color: @color-primary-3;
            }
            &-icon {
                display: flex;
                margin-left: 6px;

                font-size: 1.8rem;

                transform: rotate(180deg);

                transition: transform @duration-faster ease-in-out;
            }
        }
        &-wrapper {
            border-radius: @border-radius-lg;

            background-color: @color-gray-lighter;
        }
    }
    &__body-row-wrapper._with_details &__body-row {
        align-items: flex-start;
    }
    &__body-row-wrapper._expanded &__details-trigger-icon {
        transform: rotate(0);
    }
    &__body-row-wrapper._expanded > &__body-row {
        border-bottom: 1px solid @color-gray-light;
        border-radius: @border-radius-lg @border-radius-lg 0 0;
    }
    &__column {
        flex: 1 1 auto;
        padding: 0 8px;
        min-width: 0;
        ._autosize &:first-child,
        &._wide {
            flex: 1 1 100%;
        }
        &._fixed {
            flex: 0 0 auto;
        }
    }
    &__empty {
        .typography-small();

        padding-right: 135px;
        margin: auto;

        color: @color-gray-main;
    }
}
</style>
